/**
 * ZILURA Add to cart (product cards and product page) for the Sense theme.
 * Not in cart: stepper picks the quantity, "Add to cart" adds it.
 * In cart: the stepper edits the cart line directly (0 removes it) and the button becomes "Go to cart".
 * After each change the header cart bubble and the cart drawer are re-rendered from the Section Rendering API,
 * and the theme's cart-update event is published. Adding opens the drawer (except on the cart page) so shoppers
 * see it worked and get the "Pick 1 FREE" picker; changing quantities leaves it closed.
 */
(function () {
  if (customElements.get('zilura-atc')) return;

  const MAX_QTY = 10;
  const UPDATE_DELAY = 400;

  const cartSections = () => {
    const ids = ['cart-icon-bubble'];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    return ids;
  };

  /** Re-render the header bubble and (closed) cart drawer from a sections response */
  const renderSections = (sections) => {
    if (!sections) return;
    const parse = (html, selector) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector);

    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      const fresh = parse(sections['cart-icon-bubble'], '.shopify-section');
      if (bubble && fresh) bubble.innerHTML = fresh.innerHTML;
    }

    if (sections['cart-drawer']) {
      const drawer = document.querySelector('cart-drawer');
      const fresh = parse(sections['cart-drawer'], '#CartDrawer');
      const current = document.getElementById('CartDrawer');
      if (drawer && fresh && current) {
        current.innerHTML = fresh.innerHTML;
        const empty = !!fresh.querySelector('.drawer__inner.is-empty');
        drawer.classList.toggle('is-empty', empty);
        const overlay = document.getElementById('CartDrawer-Overlay');
        if (overlay && drawer.close) overlay.addEventListener('click', drawer.close.bind(drawer));
      }
    }
  };

  const postJSON = (url, payload) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then((r) => r.json());

  class ZiluraAtc extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.zl-atc__input');
      this.addButton = this.querySelector('button.zl-atc__btn--add');
      this.error = this.querySelector('.zl-atc__error');
      this.updateTimer = undefined;

      this.onClick = this.onClick.bind(this);
      this.onInputChange = this.onInputChange.bind(this);
      this.addEventListener('click', this.onClick);
      if (this.input) this.input.addEventListener('change', this.onInputChange);
      this.syncFormQuantity();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.input) this.input.removeEventListener('change', this.onInputChange);
    }

    get inCart() {
      return this.classList.contains('is-added');
    }

    onClick(event) {
      // Keep clicks inside the controls from opening the product page
      event.stopPropagation();

      const step = event.target.closest('[data-step]');
      if (step) {
        event.preventDefault();
        const min = this.inCart ? 0 : 1;
        this.setQuantity(Math.max(min, this.quantity() + Number(step.dataset.step)));
        if (this.inCart) this.scheduleUpdate();
        return;
      }

      if (event.target.closest('button.zl-atc__btn--add')) {
        event.preventDefault();
        this.add();
      }
    }

    onInputChange() {
      this.setQuantity(this.quantity());
      if (this.inCart) this.scheduleUpdate();
    }

    quantity() {
      const value = parseInt(this.input ? this.input.value : '1', 10);
      return Number.isNaN(value) ? 1 : value;
    }

    setQuantity(value) {
      if (!this.input) return;
      this.input.value = String(Math.min(MAX_QTY, Math.max(0, value)));
      this.syncFormQuantity();
    }

    /** Product page: keep the (hidden) theme quantity in step so "Buy it now" uses the same number */
    syncFormQuantity() {
      const form = this.closest('form');
      if (!form) return;
      // form.id would return the <input name="id"> inside the form, so read the attribute
      const formId = form.getAttribute('id');
      const qty =
        form.querySelector('input[name="quantity"]') ||
        (formId && document.querySelector(`input[name="quantity"][form="${formId}"]`));
      if (qty) qty.value = String(Math.max(1, this.quantity()));
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || '';
      this.error.hidden = !message;
    }

    /** Applies the in-cart / not-in-cart state to every control for this product on the page */
    applyState(cartQuantity) {
      document.querySelectorAll(`zilura-atc[data-product-id="${this.dataset.productId}"]`).forEach((el) => {
        el.classList.toggle('is-added', cartQuantity > 0);
        const input = el.querySelector('.zl-atc__input');
        if (input) input.value = String(cartQuantity > 0 ? cartQuantity : 1);
      });
      this.syncFormQuantity();
    }

    async cartQuantity() {
      const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      const id = Number(this.dataset.variantId);
      const qty = (cart.items || []).filter((i) => i.variant_id === id).reduce((sum, i) => sum + i.quantity, 0);
      return { cart, qty };
    }

    publishUpdate(cartData) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'zilura-atc', productVariantId: this.dataset.variantId, cartData });
      }
    }

    async add() {
      const button = this.addButton;
      if (!button || button.disabled) return;

      const label = button.querySelector('.zl-atc__label');
      const quantity = Math.max(1, this.quantity());

      button.disabled = true;
      if (label) label.textContent = 'Adding…';
      this.showError();

      try {
        const response = await postJSON(`${routes.cart_add_url}.js`, {
          items: [{ id: Number(this.dataset.variantId), quantity }],
          sections: cartSections(),
        });
        if (response.status) throw new Error(response.description || response.message || 'Could not add to cart');

        renderSections(response.sections);
        const drawer = document.querySelector('cart-drawer');
        const onCartPage = window.location.pathname === routes.cart_url;
        if (drawer && typeof drawer.open === 'function' && !onCartPage) drawer.open(button);
        const { cart, qty } = await this.cartQuantity();
        this.applyState(qty);
        this.publishUpdate(cart);
      } catch (error) {
        this.showError(error instanceof Error ? error.message : 'Could not add to cart');
      } finally {
        button.disabled = false;
        if (label) label.textContent = 'Add to cart';
      }
    }

    scheduleUpdate() {
      window.clearTimeout(this.updateTimer);
      this.updateTimer = window.setTimeout(() => this.update(), UPDATE_DELAY);
    }

    async update() {
      const quantity = this.quantity();
      this.classList.add('is-busy');
      this.showError();

      try {
        const response = await postJSON(`${routes.cart_change_url}.js`, {
          id: String(this.dataset.variantId),
          quantity,
          sections: cartSections(),
        });
        renderSections(response.sections);

        const { cart, qty } = await this.cartQuantity();
        this.applyState(qty);
        this.publishUpdate(cart);

        if (response.status || qty !== quantity) {
          this.showError(response.description || response.message || `Only ${qty} available`);
        }
      } catch (error) {
        this.showError(error instanceof Error ? error.message : 'Could not update cart');
      } finally {
        this.classList.remove('is-busy');
      }
    }
  }

  customElements.define('zilura-atc', ZiluraAtc);

  /**
   * Keep every card in step with the real cart: after a change made elsewhere (cart drawer, cart page,
   * another tab) or when the page is shown again from the back/forward cache, re-read the cart and
   * flip "Go to cart" back to "Add to cart" for products that are no longer in it (and vice versa).
   */
  const syncAll = (cart) => {
    const qtyByVariant = {};
    (cart.items || []).forEach((item) => {
      qtyByVariant[item.variant_id] = (qtyByVariant[item.variant_id] || 0) + item.quantity;
    });
    document.querySelectorAll('zilura-atc[data-variant-id]').forEach((el) => {
      const qty = qtyByVariant[Number(el.dataset.variantId)] || 0;
      el.classList.toggle('is-added', qty > 0);
      const input = el.querySelector('.zl-atc__input');
      if (input) input.value = String(qty > 0 ? qty : 1);
    });
  };

  const refreshFromCart = () => {
    if (!document.querySelector('zilura-atc')) return;
    fetch(`${window.routes ? routes.cart_url : '/cart'}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then(syncAll)
      .catch(() => {});
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'zilura-atc') refreshFromCart();
    });
  }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshFromCart();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshFromCart();
  });
})();
